// Generation "strategies" for a trip's alternatives. A strategy is the single
// per-route choice the user makes from a trip — the rest of the brief is
// inherited. The backend maps each strategy onto (transportPreference,
// sharedTransportOptions) so the existing generation prompt is reused unchanged.

import { TripParams } from './types';
import { SHARED_TRANSPORT_OPTIONS, PERSONAL_TRANSPORT_OPTIONS } from '../transport/TransportFields';

export type Strategy =
  // `emphasis` is set only by the alternative planner (same-mode variations on
  // thin briefs: a fastest vs a scenic take of one mode).
  | { kind: 'single_mode'; mode: string; emphasis?: 'fastest' | 'scenic' }
  | { kind: 'multimodal' } // mix the brief's allowed shared modes
  | { kind: 'modes'; modes: string[] } // an explicit set of shared modes (a combo)
  | { kind: 'ground_out_fly_back' }; // ground modes out, one flight home (round trips)

// One planner-chosen alternative (backend alternativePlanService): the strategy
// plus a user-facing reason. `journeyId` is stamped once the initial set is
// generated; `nudge` flags the one explore suggestion outside the selection.
export interface AlternativePlanEntry {
  id: string;
  strategy: Strategy;
  title?: string;
  reason: string;
  generate?: boolean;
  nudge?: boolean;
  journeyId?: string | null;
}

export interface AlternativePlan {
  plannedForParamsAt: string;
  plannedAt: string;
  engine: 'llm' | 'rules';
  initial: AlternativePlanEntry[];
  explore: AlternativePlanEntry[];
}

const SHARED_MODES = SHARED_TRANSPORT_OPTIONS.map((o) => o.value);
const PERSONAL_MOTORISED = ['motorcycle', 'car', 'camper_van'];

/** The trip form's "Night train" option, as the brief stores it among `sharedTransportOptions`. */
export const NIGHT_TRAIN_OPTION = 'night_train';

/**
 * The brief's shared modes with "Night train" standing for train (once, in
 * its place): a night train is a train, and a trip cannot be made of night
 * trains only. Twin of backend/services/nightTrains/formOption.js.
 */
export function modesForPlanning(sharedTransportOptions: string[] | undefined | null): string[] {
  const out: string[] = [];
  for (const m of sharedTransportOptions || []) {
    const mode = m === NIGHT_TRAIN_OPTION ? 'train' : m;
    if (!out.includes(mode)) out.push(mode);
  }
  return out;
}

// Stable key for React lists / dedupe.
export const strategyKey = (s: Strategy): string => {
  if (s.kind === 'multimodal') return 'multimodal';
  if (s.kind === 'ground_out_fly_back') return 'ground_out_fly_back';
  if (s.kind === 'modes') return `modes:${[...s.modes].sort().join('+')}`;
  return s.emphasis ? `single:${s.mode}:${s.emphasis}` : `single:${s.mode}`;
};

/**
 * Which strategy cards to show for a trip, derived from its brief's transport:
 *  - a personal motorised mode (car / camper / motorcycle) → just that one mode
 *  - none / bicycle / unset → one card per allowed shared mode, plus an
 *    "AI multimodal" card when ≥2 shared modes are allowed (with exactly one,
 *    multimodal would equal that single mode, so it's omitted).
 */
export function allowedStrategies(params: TripParams | null): Strategy[] {
  const tp = params?.transportPreference || 'none';
  if (PERSONAL_MOTORISED.includes(tp)) {
    return [{ kind: 'single_mode', mode: tp }];
  }
  const modes = modesForPlanning(params?.sharedTransportOptions).filter((m) => SHARED_MODES.includes(m));
  const list: Strategy[] = modes.map((m) => ({ kind: 'single_mode', mode: m }));
  if (modes.length >= 2) list.push({ kind: 'multimodal' });
  return list;
}

/**
 * The default strategy to auto-fire on trip creation: if the brief permits
 * exactly one transport mode, use it; otherwise pick AI multimodal.
 */
export function defaultStrategy(params: TripParams | null): Strategy | null {
  const all = allowedStrategies(params);
  if (all.length === 0) return null;
  if (all.length === 1) return all[0];
  return { kind: 'multimodal' };
}

// Display metadata (emoji + clean name) per transport mode, for the cards.
const MODE_META: Record<string, { emoji: string; name: string }> = {
  train: { emoji: '🚆', name: 'Train' },
  bus: { emoji: '🚌', name: 'Bus' },
  airplane: { emoji: '✈️', name: 'Airplane' },
  car_sharing: { emoji: '🚗', name: 'Car sharing' },
  boat: { emoji: '⛴️', name: 'Boat' },
  car: { emoji: '🚗', name: 'Car' },
  camper_van: { emoji: '🚐', name: 'Camper van' },
  motorcycle: { emoji: '🏍️', name: 'Motorcycle' },
  bicycle: { emoji: '🚲', name: 'Bicycle' },
};

export interface StrategyMeta {
  emoji: string;
  title: string;
  subtitle: string;
}

const modeName = (m: string): string => (MODE_META[m] || { name: m }).name;

// Some labels read awkwardly with "only" — these drop the suffix.
const NO_ONLY = ['camper_van', 'motorcycle'];
const singleModeLabel = (mode: string): string =>
  NO_ONLY.includes(mode) ? modeName(mode) : `${modeName(mode)} only`;
const modeEmoji = (m: string): string => (MODE_META[m] || { emoji: '🧭' }).emoji;

export function strategyMeta(s: Strategy): StrategyMeta {
  if (s.kind === 'multimodal') {
    return { emoji: '✨', title: 'Jody Smart Mix', subtitle: 'Jody mixes the best transport modes' };
  }
  if (s.kind === 'ground_out_fly_back') {
    return { emoji: '🚆✈️', title: 'Ground there, fly home', subtitle: 'Ground transport out, one flight back' };
  }
  if (s.kind === 'modes') {
    const names = s.modes.map(modeName).join(' + ');
    return { emoji: s.modes.map(modeEmoji).join(''), title: names, subtitle: 'Mix of selected modes' };
  }
  const meta = MODE_META[s.mode] || { emoji: '🧭', name: s.mode };
  if (s.emphasis) {
    return { emoji: meta.emoji, title: `${meta.name}, ${s.emphasis === 'fastest' ? 'fastest' : 'scenic'} route`, subtitle: `Whole trip by ${meta.name.toLowerCase()}` };
  }
  return { emoji: meta.emoji, title: singleModeLabel(s.mode), subtitle: `Whole trip by ${meta.name.toLowerCase()}` };
}

// Compact label for the quick-row pills.
export function strategyShortLabel(s: Strategy): string {
  if (s.kind === 'multimodal') return 'Jody Smart Mix';
  if (s.kind === 'ground_out_fly_back') return 'Ground + fly home';
  if (s.kind === 'modes') return s.modes.map(modeName).join(' + ');
  if (s.emphasis) return `${modeName(s.mode)} (${s.emphasis === 'fastest' ? 'fastest' : 'scenic'})`;
  return singleModeLabel(s.mode);
}

// Full label for the "More ways…" dropdown rows. For combos each mode keeps its
// own emoji ("🚆 Train + 🚌 Bus") rather than grouping the emojis up front.
export function strategyOptionLabel(s: Strategy): string {
  if (s.kind === 'modes') return s.modes.map((m) => `${modeEmoji(m)} ${modeName(m)}`).join(' + ');
  const m = strategyMeta(s);
  return `${m.emoji} ${m.title}`;
}

// The quick buttons for a trip that already has alternatives: the first two of
// the brief's own preferred modes (never airplane — that lives only in the
// "More ways" list), then always "Jody Smart Mix" as the third.
// NOTE: this is a deliberately simple heuristic — see the backlog item on
// analysing the existing route to make smarter, higher-value suggestions.
export function quickStrategies(params: TripParams | null): Strategy[] {
  const preferred = allowedStrategies(params)
    .filter((s) => s.kind === 'single_mode' && s.mode !== 'airplane')
    .slice(0, 2);
  return [...preferred, { kind: 'multimodal' }];
}

// The "More ways…" list: a short curated set of override options, deduped
// against whatever is already a quick button.
export function moreStrategies(params: TripParams | null): Strategy[] {
  const quickKeys = new Set(quickStrategies(params).map(strategyKey));
  const curated: Strategy[] = [
    { kind: 'modes', modes: ['train', 'bus'] },
    { kind: 'modes', modes: ['train', 'airplane'] },
    { kind: 'modes', modes: ['bus', 'airplane'] },
    { kind: 'single_mode', mode: 'car' },
    { kind: 'single_mode', mode: 'motorcycle' },
    { kind: 'single_mode', mode: 'camper_van' },
  ];
  return curated.filter((s) => !quickKeys.has(strategyKey(s)));
}

// Re-exported so consumers don't import TransportFields just for labels.
export { SHARED_TRANSPORT_OPTIONS, PERSONAL_TRANSPORT_OPTIONS };
